import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LockKeyhole } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/layout/Header";

const inputCls =
  "w-full border border-navy/15 bg-white px-4 py-3 text-sm text-charcoal outline-none transition-colors placeholder:text-charcoal/35 focus:border-gold focus:ring-1 focus:ring-gold";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem("fc_token")) navigate("/admin", { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("fc_token", data.access_token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative grid min-h-screen place-items-center bg-navy px-6" data-testid="admin-login-page">
      <div className="absolute inset-0 bg-blueprint-dark opacity-30" />
      <div className="relative z-10 w-full max-w-md border-t-4 border-gold bg-white p-10">
        <div className="flex justify-center"><Logo /></div>
        <h1 className="mt-8 flex items-center justify-center gap-2 font-heading text-xl font-extrabold tracking-tight text-navy">
          <LockKeyhole className="h-5 w-5 text-gold" />
          Admin Access
        </h1>
        <p className="mt-2 text-center text-xs text-charcoal/55">Authorised Formcore management only.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/70">Email</label>
            <input id="admin-email" data-testid="admin-email-input" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="admin@formcoreconstructions.com.au" />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/70">Password</label>
            <input id="admin-password" data-testid="admin-password-input" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
          </div>
          {error && (
            <p className="border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-btn"
            className="flex w-full items-center justify-center gap-2 bg-navy px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-charcoal active:scale-[0.98] disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <p className="mt-6 text-center">
          <Link to="/" data-testid="admin-back-to-site-link" className="text-xs font-semibold text-charcoal/50 transition-colors hover:text-gold">
            Back to website
          </Link>
        </p>
      </div>
    </div>
  );
}
